import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';
const SCENARIO_UPLOAD_TIMEOUT_MS = 30000;
const SCENARIO_RUN_TIMEOUT_MS = 180000;
const AUTH_TOKEN_STORAGE_KEY = 'scammerDetectionAuthToken';
export const AUTH_TOKEN_EXPIRED_EVENT = 'scammerDetectionAuthTokenExpired';

const getStoredToken = () => {
  try {
    return window.localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
  } catch (error) {
    return null;
  }
};

const setStoredToken = (token) => {
  try {
    if (token) {
      window.localStorage.setItem(AUTH_TOKEN_STORAGE_KEY, token);
      return;
    }

    window.localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
  } catch (error) {
    // Ignore storage errors in restricted browser contexts.
  }
};

const client = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

client.interceptors.request.use((config) => {
  const token = getStoredToken();

  if (token) {
    config.headers = {
      ...config.headers,
      Authorization: `Bearer ${token}`,
    };
  }

  return config;
});

client.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const requestUrl = error?.config?.url || '';
    const isAuthEndpoint = requestUrl.startsWith('/auth/');

    if (status === 401 && !isAuthEndpoint) {
      setStoredToken(null);
      try {
        window.dispatchEvent(new CustomEvent(AUTH_TOKEN_EXPIRED_EVENT));
      } catch (dispatchError) {
        // Ignore event dispatch issues in restricted browser contexts.
      }
    }

    return Promise.reject(error);
  }
);

export const api = {
  getAuthToken: () => getStoredToken(),

  setAuthToken: (token) => {
    setStoredToken(token);
    return token;
  },

  clearAuthToken: () => {
    setStoredToken(null);
  },

  getAuthStatus: async () => {
    const response = await client.get('/auth/status');
    return response.data;
  },

  login: async ({ username, password }) => {
    const response = await client.post('/auth/login', { username, password });
    const token = response?.data?.token;

    if (token) {
      setStoredToken(token);
    }

    return response.data;
  },

  getCurrentUser: async () => {
    const response = await client.get('/auth/me');
    return response.data;
  },

  changePassword: async ({ currentPassword, newPassword }) => {
    const response = await client.post('/auth/change-password', { currentPassword, newPassword });
    return response.data;
  },

  forgotPassword: async () => {
    const response = await client.post('/auth/forgot-password');
    return response.data;
  },

  resetPassword: async ({ token, newPassword }) => {
    const response = await client.post('/auth/reset-password', { token, newPassword });
    return response.data;
  },

  // Configuration
  getConfiguration: async () => {
    const response = await client.get('/config');
    return response.data;
  },

  updateConfiguration: async (config) => {
    const response = await client.put('/config', config);
    return response.data;
  },

  uploadRecordingDisclaimerAudio: async (payload) => {
    const response = await client.post('/config/recording-disclaimer/audio', payload, {
      timeout: SCENARIO_UPLOAD_TIMEOUT_MS,
    });
    return response.data;
  },

  // Calls
  checkNumber: async (phoneNumber) => {
    const response = await client.post('/calls/check', { phoneNumber });
    return response.data;
  },

  getCallHistory: async ({ limit, offset, filterScammers }) => {
    const response = await client.get('/calls/history', {
      params: { limit, offset, filterScammers },
    });
    return response.data;
  },

  getCallStatistics: async (period) => {
    const response = await client.get('/calls/stats', {
      params: { period },
    });
    return response.data;
  },

  getCategoryStatistics: async (period) => {
    const response = await client.get('/calls/categories', {
      params: { period },
    });
    return response.data;
  },

  getCallTrends: async (period) => {
    const response = await client.get('/calls/trends', {
      params: { period },
    });
    return response.data;
  },

  getCallsByHour: async (period) => {
    const response = await client.get('/calls/hourly', {
      params: { period },
    });
    return response.data;
  },

  getRhinoTrace: async (callId) => {
    const response = await client.get(`/calls/trace/${callId}`);
    return response.data;
  },

  getCallHistoryRecord: async (callId) => {
    const response = await client.get(`/calls/history/record/${callId}`);
    return response.data;
  },

  // Blocked Numbers
  getBlockedNumbers: async ({ limit, offset }) => {
    const response = await client.get('/blocked-numbers', {
      params: { limit, offset },
    });
    return response.data;
  },

  addBlockedNumber: async (data) => {
    const response = await client.post('/blocked-numbers', data);
    return response.data;
  },

  removeBlockedNumber: async (phoneNumber) => {
    const response = await client.delete(`/blocked-numbers/${phoneNumber}`);
    return response.data;
  },

  isNumberBlocked: async (phoneNumber) => {
    const response = await client.get(`/blocked-numbers/${phoneNumber}`);
    return response.data;
  },

  // Health
  getHealth: async () => {
    const response = await client.get('/health', {
      validateStatus: (status) => (status >= 200 && status < 300) || status === 503,
    });
    return response.data;
  },

  getMediaWorkerStatus: async () => {
    const response = await client.get('/media-workers/status');
    return response.data;
  },

  // SIP Simulator
  getSipSimulatorStatus: async () => {
    const response = await client.get('/sip-sim/status');
    return response.data;
  },

  placeSipCall: async ({ callerNumber, calleeNumber, serviceName = 'default', callerLanguage = null }) => {
    const response = await client.post('/sip-sim/call', {
      callerNumber,
      calleeNumber,
      serviceId: serviceName,
      callerLanguage: callerLanguage || null,
    });
    return response.data;
  },

  getSipSimulatorScenarios: async () => {
    const response = await client.get('/sip-sim/scenarios');
    return response.data;
  },

  uploadSipScenarioAudio: async (payload) => {
    const response = await client.post('/sip-sim/scenarios/translation-audio', payload, {
      timeout: SCENARIO_UPLOAD_TIMEOUT_MS,
    });
    return response.data;
  },

  runSipScenario: async (payload) => {
    const response = await client.post('/sip-sim/scenarios/run', payload, {
      timeout: SCENARIO_RUN_TIMEOUT_MS,
    });
    return response.data;
  },

  // Callee translation profiles and licenses
  getCalleeProfiles: async ({ limit = 100, offset = 0, search = '' } = {}) => {
    const response = await client.get('/callee-profiles', {
      params: { limit, offset, search: search || undefined },
    });
    return response.data;
  },

  getCalleeProfile: async (calleeNumber) => {
    const response = await client.get(`/callee-profiles/${encodeURIComponent(calleeNumber)}`);
    return response.data;
  },

  createCalleeProfile: async (payload) => {
    const response = await client.post('/callee-profiles', payload);
    return response.data;
  },

  updateCalleeProfile: async (calleeNumber, payload) => {
    const response = await client.put(`/callee-profiles/${encodeURIComponent(calleeNumber)}`, payload);
    return response.data;
  },

  updateCalleeLicense: async (calleeNumber, payload) => {
    const response = await client.put(`/callee-profiles/${encodeURIComponent(calleeNumber)}/license`, payload);
    return response.data;
  },

  getTranslationEligibility: async (calleeNumber, payload = {}) => {
    const response = await client.post(
      `/callee-profiles/${encodeURIComponent(calleeNumber)}/translation/eligibility`,
      payload
    );
    return response.data;
  },

  // Translation sessions
  startTranslationSession: async (payload) => {
    const response = await client.post('/translation-sessions/start', payload);
    return response.data;
  },

  stopTranslationSession: async (sessionId, payload = {}) => {
    const response = await client.post(`/translation-sessions/${encodeURIComponent(sessionId)}/stop`, payload);
    return response.data;
  },

  listTranslationSessions: async ({ limit = 100, offset = 0, status } = {}) => {
    const response = await client.get('/translation-sessions', {
      params: { limit, offset, status: status || undefined },
    });
    return response.data;
  },

  getActiveTranslationSessions: async ({ limit = 100, offset = 0 } = {}) => {
    const response = await client.get('/translation-sessions/active', {
      params: { limit, offset },
    });
    return response.data;
  },

  getTranslationCostSummary: async ({ from, to } = {}) => {
    const response = await client.get('/translation-sessions/cost-summary', {
      params: { from: from || undefined, to: to || undefined },
    });
    return response.data;
  },

  getTranslationSession: async (sessionId) => {
    const response = await client.get(`/translation-sessions/${encodeURIComponent(sessionId)}`);
    return response.data;
  },

  getTranslationSessionStatusByCall: async (callId) => {
    const response = await client.get(`/translation-sessions/call/${encodeURIComponent(callId)}/status`);
    return response.data;
  },

  getTranslationTranscriptBySession: async (sessionId) => {
    const response = await client.get(`/translation-sessions/${encodeURIComponent(sessionId)}/transcript`);
    return response.data;
  },

  getTranslationTranscriptByCall: async (callId) => {
    const response = await client.get(`/translation-sessions/call/${encodeURIComponent(callId)}/transcript`);
    return response.data;
  },

  // Pattern memory
  getPatternMemoryByCaller: async (callerId) => {
    const response = await client.get(`/pattern-memory/caller/${encodeURIComponent(callerId)}`);
    return response.data;
  },

  getPatternMemoryByVictim: async (victimId) => {
    const response = await client.get(`/pattern-memory/victim/${encodeURIComponent(victimId)}`);
    return response.data;
  },

  getPatternMemoryEntry: async (callerId, victimId) => {
    const response = await client.get(
      `/pattern-memory/caller/${encodeURIComponent(callerId)}/victim/${encodeURIComponent(victimId)}`
    );
    return response.data;
  },

  setPatternMemoryStatus: async (callerId, victimId, status) => {
    const response = await client.patch(
      `/pattern-memory/caller/${encodeURIComponent(callerId)}/victim/${encodeURIComponent(victimId)}/status`,
      { status }
    );
    return response.data;
  },
};

export default api;
